import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Footer } from 'src/components/moleculesLegacy/Footer';
import { useGetPublicCategoriesQuery } from 'src/graphql';
import * as Types from 'src/types';
import '../../../css/public/PublicReporting.css';
import CustomHeader from '../../public/CustomHeader';
import ProgressBar from '../../public/ProgressBar';
import ResponsiveContainer from '../../public/ResponsiveContainer';
import {
  CustomHeaderContainer,
  PublicReportingContainer,
} from '../../public/questions/QuestionContainer/QuestionContainer.styles';
import { Subtitle } from './Details.styles';

interface Props {
  organisation: string;
  publicReport: Types.PublicReport;
  setName(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  setEmail(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  onSuccess?: () => void;
  isAnonymousEnabledForOrg: boolean;
}

export const Details = (props: Props) => {
  const { t } = useTranslation();
  const { data: publicCategories } = useGetPublicCategoriesQuery({
    slug: props.organisation ?? '',
  });
  const selectedCategory = publicCategories?.getPublicCategories?.find(
    (cat) => cat.id === props.publicReport.reportedCategory,
  );
  const { isAnonymousEnabledForOrg, publicReport } = props;

  const showPerpetratorsQuestion = !selectedCategory?.question;
  const backLink = showPerpetratorsQuestion ? 'perpetrators' : 'file-upload';

  const disableNextButton = !isAnonymousEnabledForOrg
    ? !publicReport.name && !publicReport.email
    : false;

  return (
    <PublicReportingContainer>
      <CustomHeaderContainer>
        <CustomHeader organisation={props.organisation} />
      </CustomHeaderContainer>
      <ResponsiveContainer
        includeLeftCircleButton={true}
        includeRightCircleButton={true}
        includeBottomButton={true}
        backLinkTo={`/${props.organisation}/open-reporting/${backLink}`}
        nextLinkTo={`/${props.organisation}/open-reporting/password`}
        buttonText={t('Buttons.next')}
        buttonStyles={{ textTransform: 'uppercase' }}
        nextButtonOnClick={props.onSuccess}
        nextLinkDisabled={disableNextButton}
      >
        <div className="public-reporting-header">
          <h2 className="public-reporting-heading">
            {t('public.Details.header')}
          </h2>
          <ProgressBar percentage={90} />
        </div>

        <div className="responsive-container-body">
          <Subtitle className="public-reporting-subheading">
            {isAnonymousEnabledForOrg
              ? t('public.Details.subtitle')
              : t('public.Details.nonAnonSubtitle')}
          </Subtitle>
          <div className="public-reporting-input-container">
            <input
              data-testid="reporter-name"
              className="public-reporting-input"
              placeholder={
                isAnonymousEnabledForOrg
                  ? t('public.Details.fullNamePlaceholder')
                  : t('public.Details.nonOptionalFullNamePlaceholder')
              }
              onChange={props.setName}
              defaultValue={props.publicReport.name}
            />
          </div>
          <div className="public-reporting-input-container">
            <input
              data-testid="reporter-email"
              className="public-reporting-input"
              placeholder={
                isAnonymousEnabledForOrg
                  ? t('public.Details.emailPlaceholder')
                  : t('public.Details.nonOptionalEmailPlaceholder')
              }
              onChange={props.setEmail}
              defaultValue={props.publicReport.email}
            />
          </div>
          <p
            className="public-reporting-detail start"
            style={{ marginTop: -8 }}
          >
            {t('public.Details.disclaimer')}
          </p>
        </div>
      </ResponsiveContainer>

      <Footer id="desktop-powered-by-vault" />
    </PublicReportingContainer>
  );
};
